import React, { type FC } from 'react';
import { toGlobalID } from 'src/shared/helpers';
import type { AddPanelKey, SidePanelProduct, ProductToolData } from '../common/types';
import { AddPrintingMethodPanel } from '../addPanels/addPrintingMethodPanel';
import { AddPricingRulePanel } from '../addPanels/addPricingRulePanel';
import { AddCollageRulePanel } from '../addPanels/addCollageRulePanel';
import { AddProfanityRulePanel } from '../addPanels/addProfanityRulePanel';
import { AddAreaBackgroundRulePanel } from '../addPanels/addAreaBackgroundRulePanel';
import { AddAIStagingRulePanel } from '../addPanels/addAIStagingRulePanel';
import { AddAIVirtualModelRulePanel } from '../addPanels/addAIVirtualModelRulePanel';
import { AddMoqRulePanel } from '../addPanels/addMoqRulePanel';
import { AddQuoteRulePanel } from '../addPanels/addQuoteRulePanel';
import { AddExtraOptionPanel } from '../addPanels/addExtraOptionPanel';

export interface AddRulePanelsProps {
    activeAddPanel: AddPanelKey | null;
    product: SidePanelProduct;
    toolData: ProductToolData;
    onCloseAddPanel: () => void;
    onRefreshData: () => void;
    onNavigate: (url: string, state?: any) => void;
    onOpenToolEditor: (toolKey: string, itemId: string) => void;
}

export const AddRulePanels: FC<AddRulePanelsProps> = (props) => {
    const { activeAddPanel, product, toolData, onCloseAddPanel } = props;

    if (!activeAddPanel) return null;

    switch (activeAddPanel) {
        case 'printingMethod':
            return (
                <AddPrintingMethodPanel
                    productId={product.id}
                    existingMethodIds={(product.printTypes || []).map((pt) => toGlobalID(pt.printTypeID, 'PrintType'))}
                    onBack={onCloseAddPanel}
                    onCreateNew={() => {
                        onCloseAddPanel();
                        props.onOpenToolEditor('printing-methods', '');
                    }}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                />
            );

        case 'pricingRule':
            return (
                <AddPricingRulePanel
                    productId={product.id}
                    productModelId={product.modelID}
                    existingPricingIds={(toolData.pricingRules || []).map((pricingRule) => pricingRule.id)}
                    onBack={onCloseAddPanel}
                    onCreateNew={() => {
                        onCloseAddPanel();
                        props.onOpenToolEditor('pricing', '');
                    }}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                />
            );

        case 'collageRule':
            return (
                <AddCollageRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.collageRules || []).map((rule) => rule.id)}
                    onBack={onCloseAddPanel}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                    onCreateNew={() => {
                        props.onOpenToolEditor('collage', '');
                    }}
                />
            );

        case 'profanityRule':
            return (
                <AddProfanityRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.profanityRules || []).map((rule) => rule.id)}
                    onBack={onCloseAddPanel}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                    onCreateNew={() => {
                        onCloseAddPanel();
                        props.onOpenToolEditor('profanity', '');
                    }}
                />
            );

        case 'areaBackgroundRule':
            return (
                <AddAreaBackgroundRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.areaBackgroundRules || []).map((rule) => rule.id)}
                    onBack={onCloseAddPanel}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                    onCreateNew={() => {
                        props.onOpenToolEditor('area-background', '');
                    }}
                />
            );

        case 'aiStagingRule':
            return (
                <AddAIStagingRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.aiStagingRules || []).map((rule) => rule.id)}
                    onBack={onCloseAddPanel}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                    onCreateNew={() => {
                        onCloseAddPanel();
                        props.onOpenToolEditor('ai-lifestyle-scenes', '');
                    }}
                />
            );

        case 'aiVirtualModelRule':
            return (
                <AddAIVirtualModelRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.aiVirtualModelRules || []).map((rule) => rule.id)}
                    onBack={onCloseAddPanel}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                    onCreateNew={() => {
                        onCloseAddPanel();
                        props.onOpenToolEditor('ai-try-on', '');
                    }}
                />
            );

        case 'moqRule':
            return (
                <AddMoqRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleIds={(toolData.moqRules || []).map((rule) => rule.id)}
                    onBack={onCloseAddPanel}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                    onCreateNew={() => {
                        onCloseAddPanel();
                        props.onOpenToolEditor('moq', '');
                    }}
                />
            );

        case 'quoteRule':
            return (
                <AddQuoteRulePanel
                    productId={product.modelID}
                    productRelayId={product.id}
                    existingRuleId={toolData.quoteRule?.id || null}
                    onBack={onCloseAddPanel}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                    onCreateNew={() => {
                        onCloseAddPanel();
                        props.onOpenToolEditor('get-a-quote', '');
                    }}
                />
            );

        case 'extraOption':
            return (
                <AddExtraOptionPanel
                    productId={product.id}
                    productModelId={product.modelID}
                    existingOptionIds={(toolData.extraOptions || []).map((opt) => opt.id)}
                    onBack={onCloseAddPanel}
                    onCreateNew={() => {
                        onCloseAddPanel();
                        props.onOpenToolEditor('extra-options', '');
                    }}
                    onAssigned={() => {
                        onCloseAddPanel();
                        props.onRefreshData();
                    }}
                />
            );

        default:
            return null;
    }
};
